"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

import {
  useCallback,
  useEffect,
  useRef,
  type ChangeEvent,
  type ChangeEventHandler,
  type ComponentProps,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type Ref,
  type MutableRefObject,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { X } from "lucide-react";
import {
  FIELD_ADDON,
  FIELD_INPUT,
  FieldAction,
  FieldBox,
  useFieldContext,
  useFieldControl,
  useFormReset,
  type FieldProps,
  type FieldRadius,
  type FieldSize,
  type FieldVariant,
} from "@/components/wingo/ui/field";
import { Spinner } from "@/components/wingo/ui/spinner";
import { useControllableState } from "@/components/wingo/hooks/use-controllable-state";
import {
  durations,
  easings,
  springs,
  useMotionPreset,
  useMotionTransition,
  type MotionPreset,
} from "@/components/wingo/lib/motion";
import { useDefaults } from "@/components/wingo/lib/ui-config";
import { cn } from "@/components/wingo/lib/utils";
import { withRef } from "@/components/wingo/lib/with-ref";

export type InputType = "text" | "email" | "url" | "tel" | "search" | "password" | "number";
export type InputSize = FieldSize;
export type InputVariant = FieldVariant;
export type InputRadius = FieldRadius;
export type InputSlot =
  | "field"
  | "label"
  | "description"
  | "error"
  | "counter"
  | "box"
  | "input"
  | "prefix"
  | "suffix"
  | "leftIcon"
  | "rightIcon"
  | "clear"
  | "spinner"
  | "floatingLabel";

const TYPES: InputType[] = ["text", "email", "url", "tel", "search", "password", "number"];

// the keypad each type raises on phones. number stays a text field (no wheel or arrow stepping, and maxLength works);
// number-input has the steppers and the locale parsing
const INPUT_MODES: Record<InputType, ComponentProps<"input">["inputMode"]> = {
  text: "text",
  email: "email",
  url: "url",
  tel: "tel",
  search: "search",
  password: undefined,
  number: "decimal",
};

// the spinner matches the box's icons: 14 / 16 / 18px
const SPINNER_PX: Record<InputSize, number> = { sm: 14, md: 16, lg: 18 };

// the clear action and number-input replace the native search clear button and number spinners
const NATIVE_RESET =
  "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none";

// leading and trailing icons (decorative; svgs without a size- class follow the box: 14 / 16 / 18px)
const ICON = cn(FIELD_ADDON, "inline-flex items-center");

// deviation from DESIGN 4: a floating label needs the room, so the box is 44 / 52 / 60px (md stays 52px on touch).
// --input-float-y is how far the label rises when it floats
const FLOATING_BOX: Record<InputSize, string> = {
  sm: "h-11 [--input-float-y:8px]",
  md: "h-13 pointer-coarse:h-13 [--input-float-y:9px]",
  lg: "h-15 [--input-float-y:11px]",
};

// --input-float is 1 while the label floats: focus, a value (the input always has a placeholder, so :placeholder-shown
// means empty) or autofill (plus the -webkit- twin for older safari, its own rule so an unknown selector drops only itself).
// pure css, so it is right before hydration and for values a form library writes
const FLOAT_WHEN =
  "[--input-float:0] focus-within:[--input-float:1] has-[input:not(:placeholder-shown)]:[--input-float:1] has-[input:autofill]:[--input-float:1] has-[input:-webkit-autofill]:[--input-float:1]";
// a prefix sits where the resting label would, so the label stays up
const FLOAT_ALWAYS = "[--input-float:1]";

// the text (and text addons) sit under the floated label
const FLOATING_PAD: Record<InputSize, string> = { sm: "pt-3.5", md: "pt-4", lg: "pt-5" };

// rests centered like a placeholder, floats up and shrinks to 80%. css only: translate and scale, on the fast curve
const FLOATING_LABEL =
  "pointer-events-none absolute start-0 top-1/2 max-w-full origin-[0_50%] truncate text-muted-foreground [translate:0_calc(-50%_-_var(--input-float)_*_var(--input-float-y))] [scale:calc(1_-_var(--input-float)_*_0.2)] transition-[translate,scale,color] duration-fast ease-smooth group-data-[invalid]/field:text-destructive-soft-foreground motion-reduce:transition-colors rtl:origin-[100%_50%]";

// the clear button, the spinner and the trailing icon scale in on the snappy spring and leave quicker (W2 1.3)
const POP = {
  initial: { opacity: 0, scale: 0.6 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.6, transition: { duration: durations.fast, ease: easings.out } },
  transition: springs.snappy,
} satisfies MotionPreset;

const isNode = (node: ReactNode) => node !== undefined && node !== null && node !== false && node !== "";

// writes through the prototype setter so react's value tracker sees a change, then fires a real input event: onChange,
// onValueChange and form libraries all hear a clear like a keystroke
function setNativeValue(element: HTMLInputElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  if (setter) setter.call(element, value);
  else element.value = value;
  element.dispatchEvent(new Event("input", { bubbles: true }));
}

// React 18: dă nodul ref-ului apelantului; la detașare React cheamă din nou callback-ul cu null
function attachRef<T>(ref: Ref<T> | undefined, node: T | null) {
  if (typeof ref === "function") ref(node);
  else if (ref) (ref as MutableRefObject<T | null>).current = node;
}

// a button inside an addon (an "Apply" button) leaves focus in the input, like the box's own actions
function keepFocus(event: MouseEvent<HTMLElement>) {
  if (event.target instanceof Element && event.target.closest("button, [role='button']")) event.preventDefault();
}

export type InputProps = Omit<
  ComponentProps<"input">,
  // redefined below: our size, a ReactNode prefix, string values and the untouched native change event
  "type" | "size" | "prefix" | "value" | "defaultValue" | "onChange" | "color" | "children"
> & {
  // the native type; sets the phone keypad. number is a text field with a decimal keypad (use number-input for steppers)
  type?: InputType;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  // the native event, untouched
  onChange?: ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  // renders the field anatomy (label, description, animated error, counter) around the box
  label?: ReactNode;
  description?: ReactNode;
  // replaces the description and marks the field invalid
  error?: ReactNode;
  required?: boolean;
  // the invalid look and aria-invalid without a message
  invalid?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  // 32 / 40 / 48px (md 44px on touch); floating labels 44 / 52 / 60px
  size?: InputSize;
  variant?: InputVariant;
  radius?: InputRadius;
  // any css color for the focus ring, the outline and underline focus color and the caret
  accent?: string;
  leftIcon?: ReactNode;
  // hides while loading
  rightIcon?: ReactNode;
  // text addons ("https://", "USD") or an element (a small button, a select) inside the box
  prefix?: ReactNode;
  suffix?: ReactNode;
  // a clear button while there is a value; Escape clears too
  clearable?: boolean;
  clearLabel?: string;
  clearIcon?: ReactNode;
  // after clearing; focus stays in the input
  onClear?: () => void;
  // a small spinner at the end and aria-busy; the input stays editable
  loading?: boolean;
  // what screen readers hear for the spinner
  loadingLabel?: string;
  // the label sits inside the box and floats up on focus or when filled; needs label
  floatingLabel?: boolean;
  // the "12 / 70" counter under the box; needs maxLength
  showCount?: boolean;
  // the native limit; 0 = none
  maxLength?: number;
  // selects the text on focus (codes, amounts)
  selectOnFocus?: boolean;
  // anything else for the Field wrapper: optional, labelAction, orientation, labelWidth, hideLabel, counterLabel
  fieldProps?: Partial<FieldProps>;
  classNames?: Partial<Record<InputSlot, string>>;
};

function InputRender(inputProps: InputProps) {
  const {
    type = "text",
    value: valueProp,
    defaultValue = "",
    onValueChange,
    onChange,
    placeholder,
    label,
    description,
    error,
    required = false,
    invalid = false,
    disabled = false,
    readOnly = false,
    size = "md",
    variant = "soft",
    radius = "md",
    accent,
    leftIcon,
    rightIcon,
    prefix,
    suffix,
    clearable = false,
    clearLabel = "Clear",
    clearIcon,
    onClear,
    loading = false,
    loadingLabel = "Loading",
    floatingLabel = false,
    showCount = false,
    maxLength = 0,
    selectOnFocus = false,
    fieldProps,
    classNames,
    className,
    style,
    id,
    ref,
    inputMode,
    "aria-describedby": ariaDescribedBy,
    onFocus,
    onKeyDown,
    onMouseDown,
    onMouseUp,
    ...props
  } = useDefaults("input", inputProps);
  const parent = useFieldContext();
  const pop = useMotionPreset(POP);
  const shift = useMotionTransition(springs.snappy);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // true between the mousedown and mouseup of a click that focuses the input
  const focusingClick = useRef(false);

  const controlled = valueProp !== undefined;
  const [current, setCurrent] = useControllableState<string>({
    value: controlled ? (valueProp ?? "") : undefined,
    defaultValue,
    onChange: onValueChange,
  });
  const text = current ?? "";

  const hasLabel = isNode(label);
  // inside a <Field> the Field's label wins, so the input renders the regular way
  const floating = floatingLabel && hasLabel && !parent;
  const limit = Number.isFinite(maxLength) && maxLength > 0 ? Math.floor(maxLength) : 0;
  const kind: InputType = TYPES.includes(type) ? type : "text";

  const field = useFieldControl({
    id,
    // a floating label lives inside the box; the Field still renders the description, error and counter
    label: floating ? undefined : label,
    description,
    error,
    required,
    invalid,
    disabled,
    readOnly,
    size,
    count: text.length,
    maxLength: showCount ? limit : 0,
    fieldProps,
    classNames,
    className,
    style,
  });
  const scale = field.size;

  useEffect(() => {
    if (floatingLabel && !hasLabel && process.env.NODE_ENV !== "production")
      console.warn("[input] floatingLabel needs a label; rendering a regular input.");
  }, [floatingLabel, hasLabel]);

  // uncontrolled: a value written straight to the element (a form library, a restored form) shows up on focus and reset
  const sync = () => {
    const element = inputRef.current;
    if (element && !controlled) setCurrent(element.value);
  };
  useFormReset(inputRef, sync);

  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      (inputRef as MutableRefObject<HTMLInputElement | null>).current = node;
      attachRef(ref as Ref<HTMLInputElement> | undefined, node);
    },
    [ref],
  );

  const canClear = clearable && text !== "" && !field.disabled && !field.readOnly;
  const hasRight = isNode(rightIcon);
  const hasPrefix = isNode(prefix);

  const clear = () => {
    const element = inputRef.current;
    if (!element) return;
    setNativeValue(element, "");
    element.focus();
    onClear?.();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);
    setCurrent(event.target.value);
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    onFocus?.(event);
    sync();
    if (selectOnFocus && !event.defaultPrevented) event.currentTarget.select();
  };

  const handleMouseDown = (event: MouseEvent<HTMLInputElement>) => {
    onMouseDown?.(event);
    focusingClick.current = typeof document !== "undefined" && document.activeElement !== event.currentTarget;
  };

  const handleMouseUp = (event: MouseEvent<HTMLInputElement>) => {
    onMouseUp?.(event);
    // the mouseup that ends a focusing click would drop the selection and put the caret under the pointer
    if (selectOnFocus && focusingClick.current) event.preventDefault();
    focusingClick.current = false;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.nativeEvent.isComposing) return;
    if (event.key === "Escape" && canClear) {
      event.preventDefault();
      clear();
    }
  };

  // trailing parts slide aside when the clear button, the spinner or the trailing icon come and go. measured only when
  // one of them toggles, never per keystroke
  const moves = `${canClear}:${loading && !hasRight}:${hasRight}`;
  const slide = { layout: "position" as const, layoutDependency: moves };

  const addon = (node: ReactNode, slot: "prefix" | "suffix") => {
    if (!isNode(node)) return null;
    const plain = typeof node === "string" || typeof node === "number";
    return (
      <motion.span
        data-slot={`input-${slot}`}
        {...(slot === "suffix" ? slide : null)}
        transition={{ layout: shift }}
        onPointerDown={keepFocus}
        onMouseDown={keepFocus}
        className={cn(
          plain ? [FIELD_ADDON, "max-w-[40%] truncate"] : "inline-flex shrink-0 items-center",
          // the text sits on the input's line; a suffix fades in with the floated label, like material
          floating && plain && [FLOATING_PAD[scale], "transition-opacity duration-fast ease-smooth"],
          floating && plain && slot === "suffix" && "opacity-[var(--input-float)]",
          classNames?.[slot],
        )}
      >
        {node}
      </motion.span>
    );
  };

  const spinner = <Spinner size={SPINNER_PX[scale] ?? SPINNER_PX.md} label={loadingLabel} className="text-muted-foreground" />;

  const input = (
    <input
      {...field.controlProps}
      {...props}
      ref={setRef}
      type={kind === "number" ? "text" : kind}
      inputMode={inputMode ?? INPUT_MODES[kind]}
      data-slot="input"
      value={controlled ? text : undefined}
      defaultValue={controlled ? undefined : defaultValue}
      placeholder={floating ? placeholder || " " : placeholder || undefined}
      maxLength={limit || undefined}
      aria-describedby={[field.describedBy, ariaDescribedBy].filter(Boolean).join(" ") || undefined}
      aria-busy={loading || undefined}
      onChange={handleChange}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      className={cn(
        FIELD_INPUT,
        NATIVE_RESET,
        accent && "caret-ring",
        // the placeholder only shows under a floated label, never behind a resting one
        floating && [FLOATING_PAD[scale], "w-full placeholder:opacity-[var(--input-float)]"],
        classNames?.input,
      )}
    />
  );

  const boxStyle: CSSProperties = {
    ...(accent ? ({ "--ring": accent } as CSSProperties) : null),
    ...(field.isWrapped ? null : style),
  };

  const box = (
    <FieldBox
      data-slot="input-box"
      data-floating={floating || undefined}
      data-loading={loading || undefined}
      variant={variant}
      size={scale}
      radius={radius}
      invalid={field.invalid}
      disabled={field.disabled}
      readOnly={field.readOnly}
      className={cn(
        floating && [FLOATING_BOX[scale], hasPrefix ? FLOAT_ALWAYS : FLOAT_WHEN],
        !field.isWrapped && className,
        classNames?.box,
      )}
      style={Object.keys(boxStyle).length ? boxStyle : undefined}
    >
      {isNode(leftIcon) && (
        <span data-slot="input-left-icon" aria-hidden className={cn(ICON, classNames?.leftIcon)}>
          {leftIcon}
        </span>
      )}
      {floating ? (
        // the label is positioned against the text column (prefix included), so it starts after any leading icon and
        // floats above the prefix
        <span data-slot="input-control" className="relative flex h-full min-w-0 flex-1 items-center gap-[inherit]">
          <label
            data-slot="input-floating-label"
            id={field.labelId}
            htmlFor={field.id}
            className={cn(FLOATING_LABEL, classNames?.floatingLabel)}
          >
            {label}
            {field.required && (
              <span data-slot="input-required" aria-hidden className="ms-0.5 text-destructive-soft-foreground">
                {fieldProps?.requiredIndicator ?? "*"}
              </span>
            )}
          </label>
          {addon(prefix, "prefix")}
          {input}
        </span>
      ) : (
        <>
          {addon(prefix, "prefix")}
          {input}
        </>
      )}
      {addon(suffix, "suffix")}

      {/* without a trailing icon the spinner takes its own slot; with one, it takes the icon's place (below) */}
      <AnimatePresence initial={false} mode="popLayout">
        {loading && !hasRight && (
          <motion.span
            key="spinner"
            data-slot="input-spinner"
            {...pop}
            {...slide}
            transition={{ ...pop.transition, layout: shift }}
            className={cn("inline-flex shrink-0", classNames?.spinner)}
          >
            {spinner}
          </motion.span>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="popLayout">
        {canClear && (
          <motion.span
            key="clear"
            {...pop}
            {...slide}
            transition={{ ...pop.transition, layout: shift }}
            className="inline-flex shrink-0"
          >
            {/* out of the tab order: Escape is the keyboard path, and a stop per field would slow every form down */}
            <FieldAction
              data-slot="input-clear"
              label={clearLabel}
              icon={clearIcon ?? <X className="size-4" />}
              size={scale}
              tabIndex={-1}
              onClick={clear}
              className={classNames?.clear}
            />
          </motion.span>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="popLayout">
        {hasRight && (
          <motion.span
            key="right"
            data-slot="input-trailing"
            {...pop}
            className="grid shrink-0 place-items-center [grid-template-areas:'stack'] *:[grid-area:stack]"
          >
            {/* the icon and the spinner share one cell and crossfade */}
            <motion.span
              data-slot="input-right-icon"
              aria-hidden
              initial={false}
              animate={loading ? pop.exit : pop.animate}
              transition={pop.transition}
              className={cn(ICON, classNames?.rightIcon)}
            >
              {rightIcon}
            </motion.span>
            <AnimatePresence initial={false}>
              {loading && (
                <motion.span key="spinner" data-slot="input-spinner" {...pop} className={cn("inline-flex", classNames?.spinner)}>
                  {spinner}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.span>
        )}
      </AnimatePresence>
    </FieldBox>
  );

  return field.wrap(box);
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const Input = withRef(InputRender, "Input");
